import { TriCheckbox } from "@nova/component-checkbox"
import { SelectOutlined } from "@nova/component-select"
import { InputOutlined, TextAreaOutlined } from "@nova/ds-component-input"
import { ButtonDestructive, ButtonPrimary, ButtonTertiary } from "@nova/component-btn"
import * as Icons from "@nova/component-icons"
import { useState } from "react"
import { Divider } from "components/Divider"
import { useAppSelector } from "integrations/redux/hooks"
import { selectUserID } from "user/profile/selectors"
import { selectAllRegisteredMembers } from "features/entity-caching/members/UserManagementSlice"

export interface MaintenanceRecordFormValues {
    date_performed: string
    performed_by: string
    notes: string
    skipped: boolean
}

const MS_PER_MINUTE = 60000
const DATETIME_LOCAL_LENGTH = 16
const toLocalInput = (d: Date) =>
    new Date(d.getTime() - d.getTimezoneOffset() * MS_PER_MINUTE)
        .toISOString()
        .slice(0, DATETIME_LOCAL_LENGTH)

const maxDate = new Date()
maxDate.setMinutes(maxDate.getMinutes() - maxDate.getTimezoneOffset())
const maxDateString = maxDate.toISOString().slice(0, 16)

export function MaintenanceRecordForm({
    templateInstructions,
    initialValues,
    onSubmit,
    onCancel,
    onDelete,
    isSubmitting,
    submitLabel = "Add Record",
}: {
    templateInstructions: string | null
    initialValues?: Partial<MaintenanceRecordFormValues>
    onSubmit: (values: MaintenanceRecordFormValues) => Promise<void>
    onCancel: () => void
    onDelete?: () => Promise<void>
    isSubmitting: boolean
    submitLabel?: string
}) {
    const users = useAppSelector(selectAllRegisteredMembers)
    const [datePerformed, setDatePerformed] = useState(
        toLocalInput(
            initialValues?.date_performed ? new Date(initialValues.date_performed) : new Date()
        )
    )
    const selfId = useAppSelector(selectUserID)
    const [performedBy, setPerformedBy] = useState(initialValues?.performed_by ?? selfId ?? "")
    const [notes, setNotes] = useState(initialValues?.notes ?? "")
    const [skipped, setSkipped] = useState(initialValues?.skipped ?? false)

    const userOptions = (users ?? [])
        .map((u) => ({ label: u.fullName, value: u.id }))
        .filter((u): u is { label: string; value: string } => !!u.label && !!u.value)

    const canSubmit = !!datePerformed && !isSubmitting

    const handleSubmit = () =>
        onSubmit({
            date_performed: new Date(datePerformed).toISOString(),
            performed_by: performedBy,
            notes,
            skipped,
        })

    return (
        <div className="flex flex-col gap-5 pt-2">
            {templateInstructions && (
                <div className="flex flex-col gap-2.5">
                    <h4 className="text-gray-100">Instructions</h4>
                    <h5 className="text-gray-50">{templateInstructions}</h5>
                    <Divider />
                </div>
            )}

            <div className="grid grid-cols-2 items-center gap-4">
                <InputOutlined
                    type="datetime-local"
                    label="Date & Time Performed"
                    value={datePerformed}
                    onChange={(e) => setDatePerformed(e.currentTarget.value)}
                    max={maxDateString}
                />

                <div className="flex flex-col gap-2">
                    <label className="text-gray-100">Performed By</label>
                    <SelectOutlined
                        options={userOptions}
                        value={performedBy}
                        onChange={setPerformedBy}
                        placeholder="Select person"
                    />
                </div>
            </div>

            <TextAreaOutlined
                label="Notes"
                value={notes}
                onChange={(e) => setNotes(e.currentTarget.value)}
                rows={4}
            />

            <div className="flex items-center gap-3 cursor-pointer">
                <TriCheckbox
                    state={skipped ? "checked" : "unchecked"}
                    onClick={() => setSkipped((s) => !s)}
                />
                <span className="text-sm text-gray-300">Skip this maintenance</span>
            </div>

            {skipped && (
                <div className="flex flex-col gap-1">
                    <h5 className="text-orange-200">
                        Only skip if this service was not completed.
                    </h5>
                    <div className="flex flex-col gap-1">
                        <h5 className="text-gray-0">Skipping this maintenance will:</h5>
                        <ul className="list-disc list-inside flex flex-col">
                            <li>
                                <h5 className="text-gray-0 inline">
                                    Record the service as skipped
                                </h5>
                            </li>
                            <li>
                                <h5 className="text-gray-0 inline">
                                    Reset the tracking of this service
                                </h5>
                            </li>
                            <li>
                                <h5 className="text-gray-0 inline">
                                    Review the drone&apos;s condition
                                </h5>
                            </li>
                        </ul>
                    </div>
                </div>
            )}

            <div className="flex justify-between items-center">
                {onDelete && (
                    <ButtonDestructive
                        title="Delete Record"
                        LeftIcon={Icons.Trash}
                        onClick={onDelete}
                        disabled={isSubmitting}
                    />
                )}
                <div className="flex gap-2 ml-auto">
                    <ButtonTertiary title="Cancel" onClick={onCancel} />
                    <ButtonPrimary
                        title={submitLabel}
                        onClick={handleSubmit}
                        disabled={!canSubmit}
                    />
                </div>
            </div>
        </div>
    )
}
